import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBrand } from "@/context/BrandContext";
import api from "@/lib/api";
import {
  ArrowRight, CheckCircle, X, List, Phone, Star, ShieldCheck, Headset,
  GlobeHemisphereEast, Lightning, SimCard, DeviceMobile, WifiHigh, House,
} from "@phosphor-icons/react";

const C = {
  bg: "#070B16", surface: "#0F1729", card: "#111a2e", green: "#10B981", orange: "#FF7A00",
  text: "#F8FAFC", sub: "#94A3B8", border: "rgba(255,255,255,0.10)",
};
const HEAD = { fontFamily: "'Outfit', sans-serif" };
const BODY = { fontFamily: "'Manrope', sans-serif" };
const SUPPORT_WA = "https://wa.me/12029403399?text=" + encodeURIComponent("Hi! I'd like help with an iYeneh phone plan.");

const ICONS = { DeviceMobile, WifiHigh, SimCard, Phone, House, GlobeHemisphereEast };
const HERO = {
  mobile: {
    badge: "Mobile Wireless — No Contract",
    title: <>Big-carrier coverage.<br /><span style={{ color: C.orange }}>None of the contracts.</span></>,
    sub: "Nationwide 5G on a plan you control. Keep your number, cancel anytime, and pay from the same iYeneh wallet that powers your calls and services.",
  },
  landline: {
    badge: "Home & Business VoIP — No Contract",
    title: <>Internet phone lines<br /><span style={{ color: C.orange }}>that never miss a call.</span></>,
    sub: "Crystal-clear VoIP for home and business — SI receptionist, voicemail-to-email, and your existing number, all over your internet. No contract, cancel anytime.",
  },
};

export default function Wireless() {
  const brand = useBrand();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const [cats, setCats] = useState([]);
  const [plans, setPlans] = useState([]);
  const [active, setActive] = useState("mobile");
  const [annual, setAnnual] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/telecom/service-plans")
      .then(({ data }) => {
        setCats(data.categories || []);
        setPlans(data.plans || []);
        if ((data.categories || []).length && !(data.categories || []).some((c) => c.key === "mobile")) {
          setActive(data.categories[0].key);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const shown = useMemo(() => plans.filter((p) => p.category === active).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)), [plans, active]);
  const hero = HERO[active] || HERO.mobile;

  const choose = (key) => {
    try { localStorage.setItem("iyeneh_wireless_intent", key); } catch (e) {}
    navigate(`/checkout?wireless=${key}${annual ? "&billing=annual" : ""}`);
  };

  return (
    <div style={{ ...BODY, background: C.bg, color: C.text }} className="min-h-screen overflow-x-hidden" data-testid="plans-page">
      <header className="sticky top-0 z-50 border-b bg-[#0B1220]/85 backdrop-blur-xl" style={{ borderColor: C.border }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" data-testid="plans-logo" className="flex items-center gap-2">
            <img src="/icons/icon-512.png" alt="iYeneh Connect" className="h-9 w-9 sm:h-10 sm:w-10" />
            <span className="text-lg font-bold tracking-tight text-white sm:text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>iYeneh Connect</span>
          </Link>
          <nav className="hidden items-center gap-7 md:flex">
            <Link to="/pricing" className="text-sm font-medium text-slate-300 hover:text-white">Cloud PBX</Link>
            <Link to="/wireless" className="text-sm font-semibold text-white">Plans</Link>
            <Link to="/shop" className="text-sm font-medium text-slate-300 hover:text-white">Shop Devices</Link>
            <a href={SUPPORT_WA} target="_blank" rel="noreferrer" className="text-sm font-medium text-slate-300 hover:text-white">Support</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" data-testid="plans-signin" className="hidden text-sm font-semibold text-slate-200 hover:text-white sm:block">Sign In</Link>
            <Link to="/register" data-testid="plans-create-account" className="rounded-full px-4 py-2.5 text-sm font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5" style={{ background: C.orange }}>Create Free Account</Link>
            <button onClick={() => setMenu((m) => !m)} className="text-slate-200 md:hidden" aria-label="Menu">{menu ? <X size={24} /> : <List size={24} />}</button>
          </div>
        </div>
        {menu && (
          <div className="border-t md:hidden" style={{ borderColor: C.border }}>
            <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3">
              <Link to="/pricing" onClick={() => setMenu(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/5">Cloud PBX</Link>
              <Link to="/shop" onClick={() => setMenu(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/5">Shop Devices</Link>
              <a href={SUPPORT_WA} target="_blank" rel="noreferrer" className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/5">Support</a>
              <Link to="/login" onClick={() => setMenu(false)} className="rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-200 hover:bg-white/5">Sign In</Link>
            </nav>
          </div>
        )}
      </header>

      <section className="mx-auto max-w-4xl px-4 pt-14 pb-6 text-center sm:px-6 sm:pt-20">
        <div className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: "#FF7A001a", color: C.orange }}>
          <SimCard size={14} weight="fill" /> {hero.badge}
        </div>
        <h1 style={HEAD} className="text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">{hero.title}</h1>
        <p style={{ color: C.sub }} className="mx-auto mt-5 max-w-2xl text-base leading-relaxed sm:text-lg">{hero.sub}</p>
      </section>

      {/* Trust strip */}
      <div className="mx-auto mb-6 flex max-w-3xl flex-wrap items-center justify-center gap-x-5 gap-y-2 px-4 text-xs font-semibold sm:text-sm" style={{ color: C.sub }} data-testid="plans-trust">
        {["No contract, ever", "Keep your number", "Activate in minutes", "30-day money-back"].map((t) => (
          <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle size={15} weight="fill" style={{ color: C.green }} /> {t}</span>
        ))}
      </div>

      {/* Billing toggle */}
      <div className="mx-auto mb-5 flex max-w-md items-center justify-center gap-3 px-4">
        <span className="text-sm font-semibold" style={{ color: annual ? C.sub : C.text }}>Monthly</span>
        <button data-testid="billing-toggle" onClick={() => setAnnual((a) => !a)} className="relative h-7 w-14 shrink-0 rounded-full transition-colors" style={{ background: annual ? C.green : "rgba(255,255,255,0.16)" }} aria-label="Toggle annual billing">
          <span className="absolute top-1 h-5 w-5 rounded-full bg-white transition-all" style={{ left: annual ? "34px" : "4px" }} />
        </button>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: annual ? C.text : C.sub }}>Annual <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: "#10B98122", color: C.green }}>2 months free</span></span>
      </div>

      {/* Category tabs */}
      <div className="mx-auto mb-2 flex max-w-md items-center gap-1 rounded-full border p-1 sm:mb-4" style={{ borderColor: C.border, background: C.surface }} data-testid="plans-tabs">
        {(cats.length ? cats : [{ key: "mobile", name: "Mobile Wireless" }]).map((c) => (
          <button key={c.key} data-testid={`plans-tab-${c.key}`} onClick={() => setActive(c.key)}
            className="flex-1 rounded-full px-4 py-2.5 text-sm font-bold transition-colors"
            style={{ background: active === c.key ? C.orange : "transparent", color: active === c.key ? "#fff" : C.sub }}>
            {c.name}
          </button>
        ))}
      </div>

      <section className={`mx-auto grid max-w-6xl gap-6 px-4 pb-6 pt-4 sm:px-6 ${shown.length >= 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
        {loading && <p className="col-span-full py-10 text-center text-sm" style={{ color: C.sub }}>Loading plans…</p>}
        {!loading && shown.length === 0 && <p className="col-span-full py-10 text-center text-sm" style={{ color: C.sub }}>No plans available yet.</p>}
        {shown.map((p) => {
          const Icon = ICONS[p.icon] || Phone;
          const accent = p.accent || C.green;
          return (
            <div key={p.key} data-testid={`plan-card-${p.key}`}
              className={`relative flex flex-col rounded-3xl border p-7 transition-transform hover:-translate-y-1 ${p.popular ? "lg:-mt-4 lg:mb-4 lg:scale-[1.03]" : ""}`}
              style={{ background: p.popular ? "linear-gradient(180deg,#16233f 0%,#0F1729 100%)" : C.card, borderColor: p.popular ? C.orange : C.border, boxShadow: p.popular ? "0 20px 60px -20px rgba(255,122,0,0.45)" : "none" }}>
              {p.popular && (<span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-4 py-1 text-xs font-extrabold uppercase tracking-wide text-white" style={{ background: C.orange }}>Most Popular</span>)}
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${accent}22`, color: accent }}><Icon size={22} weight="fill" /></div>
                <div>
                  <p style={HEAD} className="text-lg font-extrabold">{p.name}</p>
                  <p className="text-xs font-semibold" style={{ color: accent }}>{p.nickname}</p>
                </div>
              </div>
              <div className="mt-6 flex items-end gap-1">
                <span style={HEAD} className="text-5xl font-black">${annual ? Math.round(p.price_cents * 10 / 12 / 100) : Math.round(p.price_cents / 100)}</span>
                <span className="mb-1.5 text-sm" style={{ color: C.sub }}>/month</span>
              </div>
              {annual
                ? <p className="mt-1 text-xs font-semibold" style={{ color: C.green }} data-testid={`annual-note-${p.key}`}>Billed ${Math.round(p.price_cents * 10 / 100)}/yr — save ${Math.round(p.price_cents * 2 / 100)}</p>
                : <p className="mt-1 text-xs" style={{ color: C.sub }}>Billed monthly · cancel anytime</p>}
              {p.headline && <p style={HEAD} className="mt-4 min-h-[3.5rem] text-lg font-bold leading-snug">“{p.headline}”</p>}
              <button data-testid={`plan-cta-${p.key}`} onClick={() => choose(p.key)}
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-bold text-white transition-transform hover:scale-[1.02]"
                style={{ background: p.popular ? C.orange : "rgba(255,255,255,0.08)", border: p.popular ? "none" : `1px solid ${C.border}` }}>
                {p.cta || `Get ${p.name}`} <ArrowRight size={18} weight="bold" />
              </button>
              <ul className="mt-6 space-y-3">
                {(p.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <CheckCircle size={18} weight="fill" style={{ color: accent }} className="mt-0.5 shrink-0" />
                    <span className="text-slate-200">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-12 sm:px-6">
        <div className="flex items-start gap-3 rounded-2xl border p-5" style={{ borderColor: C.border, background: C.surface }}>
          <Lightning size={22} weight="fill" style={{ color: C.green }} className="mt-0.5 shrink-0" />
          <p className="text-sm leading-relaxed" style={{ color: C.sub }}>
            <span className="font-semibold text-slate-100">How it works:</span> pick a plan and create your free account — an iYeneh specialist activates your line, SIM or eSIM and helps you port your number. No contracts, no credit checks, cancel anytime.
          </p>
        </div>
      </section>

      <section className="border-t" style={{ borderColor: C.border, background: C.surface }}>
        <div className="mx-auto grid max-w-6xl gap-6 px-4 py-12 sm:grid-cols-3 sm:px-6">
          {[
            { icon: ShieldCheck, t: "No contract, ever", d: "Change or cancel your plan whenever you want." },
            { icon: GlobeHemisphereEast, t: "Nationwide coverage", d: "Fast, reliable 5G mobile & clear landline service." },
            { icon: Headset, t: "Real human support", d: "Plus a 24/7 SI team that never sleeps." },
          ].map((b) => (
            <div key={b.t} className="flex items-start gap-3">
              <b.icon size={24} weight="fill" style={{ color: C.orange }} className="mt-0.5 shrink-0" />
              <div><p style={HEAD} className="font-bold">{b.t}</p><p className="text-sm" style={{ color: C.sub }}>{b.d}</p></div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6">
        <Phone size={36} weight="fill" style={{ color: C.orange }} className="mx-auto" />
        <h2 style={HEAD} className="mt-4 text-3xl font-black sm:text-4xl">Switch in minutes. Keep your number.</h2>
        <p className="mx-auto mt-3 max-w-xl text-base" style={{ color: C.sub }}>Create your free account and we'll handle the rest.</p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <button data-testid="plans-final-cta" onClick={() => choose(shown[0]?.key || "wireless_plus")} className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold text-white shadow-xl transition-transform hover:-translate-y-1" style={{ background: C.orange }}>
            Get started <ArrowRight size={18} weight="bold" />
          </button>
          <a href={SUPPORT_WA} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 px-8 py-4 font-semibold text-white transition-colors hover:bg-white/10" style={{ borderColor: "rgba(255,255,255,0.4)" }}>
            <Star size={18} weight="fill" /> Talk to a specialist
          </a>
        </div>
      </section>

      <footer className="border-t px-4 py-6 text-center text-xs sm:px-6" style={{ borderColor: C.border, color: C.sub, background: "#0B1220" }}>
        © {new Date().getFullYear()} {brand.legal_name || brand.brand_name || "Capital Defense Institute LLC"} ·{" "}
        <Link to="/privacy" className="hover:text-white">Privacy</Link> ·{" "}
        <Link to="/terms" className="hover:text-white">Terms</Link> ·{" "}
        <Link to="/pricing" className="hover:text-white">Cloud PBX</Link>
      </footer>
    </div>
  );
}
